import { useState } from 'react';
import { AlertCircle, Download, Loader2, Trash2, Upload } from 'lucide-react';
import type { FormField, FormSchemaDocument } from '@mygmao/shared';
import { validateCustomFields } from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';

/**
 * Représentation d'un fichier uploadé via le champ Form Builder type=file.
 * Stocké tel quel dans `custom_fields[key]` à la soumission du formulaire.
 */
export interface FileFieldValue {
  /** Clé S3 (préfixée par tenant_id) — utilisée pour re-sign + delete. */
  key: string;
  /** URL signée fraîche (TTL 1h). À ré-émettre via /uploads/:key/signed-url. */
  signed_url: string;
  original_name: string;
  mime_type: string;
  size: number;
}

/**
 * Composant `<DynamicFormRenderer />` (US-ADM-08).
 *
 * Rend dynamiquement un formulaire à partir d'un FormSchema. Utilisé par
 * tous les modules métier (`UserFormModal`, `EquipementFormModal`, etc.)
 * pour afficher les champs additionnels configurés par l'admin tenant.
 *
 * Gère :
 *  - les 16 types de champs
 *  - les conditions `visible_if`
 *  - la validation côté client (réutilise `validateCustomFields` du shared)
 *
 * Le bouton submit reste à la charge du parent (qui consomme `values` +
 * `errors` exposés par `onChange`).
 */

interface DynamicFormRendererProps {
  schema: FormSchemaDocument;
  values: Record<string, unknown>;
  errors?: Record<string, string[]>;
  onChange(values: Record<string, unknown>): void;
  disabled?: boolean;
}

export function DynamicFormRenderer({
  schema,
  values,
  errors,
  onChange,
  disabled = false,
}: DynamicFormRendererProps) {
  function setField(key: string, value: unknown) {
    onChange({ ...values, [key]: value });
  }

  return (
    <div className="space-y-4">
      {schema.fields.map((field, idx) => {
        if (!isVisible(field, values)) return null;
        if (field.type === 'section_title') {
          return (
            <h3
              key={`section-${idx}`}
              className="text-sm font-semibold text-gray-700 uppercase tracking-wide pt-2"
            >
              {field.label}
            </h3>
          );
        }
        if (field.type === 'separator') {
          return <hr key={`sep-${idx}`} className="border-gray-200" />;
        }
        const fieldErrors = errors?.[field.key] ?? [];
        return (
          <FieldInput
            key={field.key}
            field={field}
            value={values[field.key]}
            errors={fieldErrors}
            disabled={disabled}
            onChange={(v) => setField(field.key, v)}
          />
        );
      })}
    </div>
  );
}

/**
 * Hook simple — gère un état local de form values + validation à la
 * demande. À utiliser par les pages qui veulent un comportement contrôlé.
 */
export function useDynamicForm(
  schema: FormSchemaDocument | null,
  initialValues: Record<string, unknown> = {},
) {
  const [values, setValues] = useState<Record<string, unknown>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  function validate(): { valid: boolean; values: Record<string, unknown> } {
    if (!schema) return { valid: true, values };
    const result = validateCustomFields(schema, values);
    if (!result.valid) {
      setErrors(result.errors);
      return { valid: false, values };
    }
    setErrors({});
    return { valid: true, values: result.values };
  }

  return { values, errors, setValues, validate };
}

// ─── Champs internes ────────────────────────────────────────────────────────

interface FieldInputProps {
  field: FormField;
  value: unknown;
  errors: string[];
  disabled?: boolean;
  onChange(v: unknown): void;
}

function FieldInput({ field, value, errors, disabled, onChange }: FieldInputProps) {
  const label = (
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {field.label}
      {field.required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
  const errorEl =
    errors.length > 0 ? (
      <p className="text-red-500 text-xs mt-1">{errors.join(' · ')}</p>
    ) : field.help ? (
      <p className="text-gray-400 text-xs mt-1">{field.help}</p>
    ) : null;

  const cls = inputClass(disabled);

  switch (field.type) {
    case 'textarea':
      return (
        <div>
          {label}
          <textarea
            rows={3}
            placeholder={field.placeholder}
            disabled={disabled}
            className={cls}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
          {errorEl}
        </div>
      );
    case 'number':
      return (
        <div>
          {label}
          <input
            type="number"
            placeholder={field.placeholder}
            disabled={disabled}
            className={cls}
            value={(value as number | string | undefined) ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
          />
          {errorEl}
        </div>
      );
    case 'date':
      return (
        <div>
          {label}
          <input
            type="date"
            disabled={disabled}
            className={cls}
            value={toDateInputValue(value, 'date')}
            onChange={(e) => onChange(e.target.value || undefined)}
          />
          {errorEl}
        </div>
      );
    case 'datetime':
      return (
        <div>
          {label}
          <input
            type="datetime-local"
            disabled={disabled}
            className={cls}
            value={toDateInputValue(value, 'datetime')}
            onChange={(e) => onChange(e.target.value || undefined)}
          />
          {errorEl}
        </div>
      );
    case 'boolean':
      return (
        <div className="flex items-center space-x-2">
          <input
            id={field.key}
            type="checkbox"
            disabled={disabled}
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
          />
          <label htmlFor={field.key} className="text-sm text-gray-700">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {errorEl}
        </div>
      );
    case 'select':
      return (
        <div>
          {label}
          <select
            disabled={disabled}
            className={cls}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value || undefined)}
          >
            <option value="">— Sélectionner —</option>
            {(field.options ?? []).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errorEl}
        </div>
      );
    case 'multiselect': {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div>
          {label}
          <div className="space-y-1">
            {(field.options ?? []).map((opt) => (
              <label key={opt.value} className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  disabled={disabled}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  checked={arr.includes(opt.value)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...arr, opt.value]
                      : arr.filter((v) => v !== opt.value);
                    onChange(next);
                  }}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
          {errorEl}
        </div>
      );
    }
    case 'radio':
      return (
        <div>
          {label}
          <div className="space-y-1">
            {(field.options ?? []).map((opt) => (
              <label key={opt.value} className="flex items-center space-x-2 text-sm">
                <input
                  type="radio"
                  name={field.key}
                  disabled={disabled}
                  className="w-4 h-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                  checked={value === opt.value}
                  onChange={() => onChange(opt.value)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
          {errorEl}
        </div>
      );
    case 'email':
    case 'phone':
    case 'url':
      return (
        <div>
          {label}
          <input
            type={field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'tel'}
            placeholder={field.placeholder}
            disabled={disabled}
            className={cls}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
          {errorEl}
        </div>
      );
    case 'file':
      return (
        <FileFieldInput
          field={field}
          value={value as FileFieldValue | undefined}
          onChange={onChange}
          disabled={disabled}
          errorEl={errorEl}
          label={label}
        />
      );
    case 'json':
      return (
        <div>
          {label}
          <textarea
            rows={4}
            disabled={disabled}
            className={`${cls} font-mono`}
            value={value === undefined ? '' : JSON.stringify(value, null, 2)}
            onChange={(e) => {
              try {
                onChange(e.target.value === '' ? undefined : JSON.parse(e.target.value));
              } catch {
                // accepte texte brut tant que pas parseable — sera rejeté à la validation
                onChange(e.target.value);
              }
            }}
          />
          {errorEl}
        </div>
      );
    case 'text':
    default:
      return (
        <div>
          {label}
          <input
            type="text"
            placeholder={field.placeholder}
            disabled={disabled}
            className={cls}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
          {errorEl}
        </div>
      );
  }
}

function isVisible(field: FormField, values: Record<string, unknown>): boolean {
  if (!field.visible_if) return true;
  const referenced = values[field.visible_if.field];
  return JSON.stringify(referenced) === JSON.stringify(field.visible_if.equals);
}

function inputClass(disabled?: boolean): string {
  const base =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm';
  return disabled ? `${base} bg-gray-50 cursor-not-allowed` : base;
}

function toDateInputValue(value: unknown, kind: 'date' | 'datetime'): string {
  if (!value) return '';
  if (typeof value === 'string') {
    if (kind === 'date') return value.slice(0, 10);
    return value.slice(0, 16);
  }
  return '';
}

/**
 * Input fichier branché sur l'endpoint backend `/uploads/custom-field`.
 * Stocke le résultat (clé MinIO + URL signée + metadata) dans
 * `custom_fields[field.key]` à la soumission du formulaire.
 */
function FileFieldInput({
  field,
  value,
  onChange,
  disabled,
  errorEl,
  label,
}: {
  field: FormField;
  value: FileFieldValue | undefined;
  onChange(v: FileFieldValue | undefined): void;
  disabled?: boolean;
  errorEl: React.ReactNode;
  label: React.ReactNode;
}) {
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handlePick(file: File) {
    setUploading(true);
    setErrorMessage(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post<{ data: FileFieldValue }>('/uploads/custom-field', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove() {
    if (!value) return;
    if (!confirm('Supprimer ce fichier ?')) return;
    try {
      await api.delete(`/uploads/${encodeURIComponent(value.key)}`);
    } catch {
      // Best-effort.
    }
    onChange(undefined);
  }

  return (
    <div>
      {label}
      {value ? (
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
          <div className="flex items-center min-w-0 flex-1">
            <Download className="w-4 h-4 mr-2 text-primary-600 flex-shrink-0" />
            <a
              href={value.signed_url}
              target="_blank"
              rel="noreferrer"
              className="truncate text-primary-600 hover:underline"
            >
              {value.original_name}
            </a>
            <span className="ml-2 text-xs text-gray-400 flex-shrink-0">
              ({(value.size / 1024).toFixed(1)} ko)
            </span>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleRemove}
              className="ml-2 p-1.5 rounded text-red-600 hover:bg-red-50"
              aria-label="Supprimer le fichier"
              title="Supprimer le fichier"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : (
        <label
          className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer ${
            disabled || uploading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          {uploading ? 'Upload en cours…' : 'Choisir un fichier'}
          <input
            type="file"
            accept={field.validation?.accept}
            disabled={disabled || uploading}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handlePick(f);
              e.target.value = '';
            }}
          />
        </label>
      )}
      {field.validation?.max_size_mb && (
        <p className="text-xs text-gray-400 mt-1">
          Max {field.validation.max_size_mb} Mo
          {field.validation.accept && ` · Formats : ${field.validation.accept}`}
        </p>
      )}
      {errorMessage && (
        <p className="text-xs text-red-600 mt-1 inline-flex items-center">
          <AlertCircle className="w-3 h-3 mr-1" />
          {errorMessage}
        </p>
      )}
      {errorEl}
    </div>
  );
}
